













































import Scheduler from '@/core/components/proxies/Scheduler.vue';
import { EventRecord, DragEvent, ResizeEvent } from '@/core/components/proxies/types';
import { getTableCaption } from '@/core/components/table';
import { Component, Prop, Ref } from 'vue-property-decorator';
import { Page } from '@/core/components/controls';
import { Crane, CraneDict, LogisticOperation, PlannedOperationType, Site, SiteDict } from '@/dto';
import { localize, date } from '@/core/filters';
import { encodeParams, guid, Inject, lookupField } from '@/core/helpers';
import {
    ConfigServiceContract,
    CONTRACTS,
    Events,
    EventsServiceContract,
    FetchServiceContract
} from '@/core/contracts';
import { ContextsState } from '@/store';
import container from '@/container';
import { DateTime, Duration, Interval } from 'luxon';
import { DxLoadIndicator } from 'devextreme-vue/load-indicator';
import DxSelectBox from 'devextreme-vue/select-box';
import _ from 'lodash';

interface LogisticWorkshopEvent {
  resourceId: string;
  resourceAlias: string;
  startDate?: Date;
  endDate?: Date;
  linkedResource: string;
  startSite: string;
  finishSite: string;
  name: string;
  eventColor?: string;
  operationId: number;
  id?: string;
  type: PlannedOperationType;
  timeDuration?: string;
  productTypes: string;
  volume?: number;
  area?: string;
  packs?: number;
  fixed?: boolean;
}

@Component({
    components: {
        Scheduler,
        DxLoadIndicator,
        DxSelectBox
    }
})
export default class LogisticWorkplacePage extends Page {
    public activeBox: boolean = false;
    public operations: LogisticOperation[] = [];
    public cranes: CraneDict | null = null;
    public sites: SiteDict | null = null;
    public intervals: any | null = null;
    public currentSite: Site | null = null;
    public startDate: Date | null = null;
    public endDate: Date | null = null;
    public loading: boolean = false;
    public selectedOperationId: number | null = null;
    public syntaskHost: string | undefined = '';

    @Inject(CONTRACTS.EventsService)
    private _eventsService?: EventsServiceContract;

    @Inject(CONTRACTS.ConfigService)
    private _configService?: ConfigServiceContract;

    @Inject(CONTRACTS.FetchService)
    private _fetchService?: FetchServiceContract;

    public get pageName(): string {
        return this.$l.LogisticWorkplace.PageLabel;
    }

    public async created() {
        this.syntaskHost = this._configService?.getConfig().Services?.syntaskURL;
        await this.loadResources();
        await this.loadSites();
        await this.loadIntervals();
        await this.loadEvents();
    }

    public async mounted() {
      this._eventsService!.on(
        Events.ProcessingFinished,
        this.onProcessingFinished
      );
    }

    public async beforeDestroy() {
      this._eventsService!.off(
        Events.ProcessingFinished,
        this.onProcessingFinished
      );
    }

    public get resources() {
        if (this.cranes) {
            let result: Crane[] = [];
            for (const contextId in this.cranes) {
                if (this.cranes.hasOwnProperty(contextId)) {
                    result = [
                        ...result,
                        ...this.cranes[contextId]
                            .map(r => ({
                                ...r,
                                id: `${r.id}_${contextId}`, activitySchedule: `${r.activitySchedule}_${contextId}`
                            }))
                        ];
                }
            }
            return result.sort((a, b) => a.name > b.name ? 1 : a.name < b.name ? -1 : 0);
        }
        return [];
    }

    public get siteList() {
        if (this.sites) {
            let result: Site[] = [];
            for (const contextId in this.sites) {
                if (this.sites.hasOwnProperty(contextId)) {
                    result = [
                        ...result,
                        ...this.sites[contextId]
                            .map(s => ({...s, alias: `${s.id}_${contextId}`}))
                        ];
                }
            }
            return result;
        }
        return [];
    }

    public get resourceTimeRanges() {
        if (this.intervals) {
            let result: any[] = [];
            for (const contextId in this.intervals) {
                if (this.intervals.hasOwnProperty(contextId)) {
                    const intervals = this.intervals[contextId];
                    const resourceIntervals = intervals.craneIntervals.map((x: any) => ({
                        id: guid(),
                        resourceId: `${x.alias}_${contextId}`,
                        startDate: x.startTime,
                        endDate: DateTime.fromISO(x.startTime)
                            .plus(Duration.fromISO(x.duration)).toISO(),
                        style : 'background: rgba(255,0,0,.3);',
                        type: 'crane'
                    }));
                    const activityIntervals = this.resources ? intervals.activityIntervals
                        .reduce((acc: any[], x: any) => {
                            const resources = this.resources
                                .filter(res => res.activitySchedule === `${x.alias}_${contextId}`);
                            return [...acc, ...resources.map(res => ({
                                id: guid(),
                                resourceId: res.id,
                                startDate: x.startTime,
                                endDate: DateTime.fromISO(x.startTime)
                                    .plus(Duration.fromISO(x.duration)).toISO(),
                                style : 'background: rgba(255,165,0,.3);',
                                type: 'activity'
                            }))];
                        }, []) : [];
                    const siteIntervals = this.currentSite && this.resources ? intervals.siteIntervals
                        .filter((x: any) => `${x.alias}_${contextId}` === this.currentSite!.alias)
                        .reduce((acc: any[], x: any) => {
                            return [...acc, ...this.resources.map(res => ({
                                id: guid(),
                                resourceId: res.id,
                                startDate: x.startTime,
                                endDate: DateTime.fromISO(x.startTime)
                                    .plus(Duration.fromISO(x.duration)).toISO(),
                                style : 'background: rgba(0,255,0,.3);',
                                type: 'site'
                            }))];
                        }, []) : [];
                    result = [...result, ...resourceIntervals, ...activityIntervals, ...siteIntervals];
                }
            }
            return result;
        }
        return [];
    }

    public get events() {
        return this.operations
            ? this.operations
                  .filter(x => !this.currentSite || this.currentSite.alias === `${x.site!.id}_${x.sourceContextId}`)
                  .reduce((result: any[], x) => [...result, ...this.operationToEvents(x)], [])
            : [];
    }

    public get columns() {
        return [
            {
                name: this.$l.LogisticWorkplace.ResourceColumn,
                field: 'name',
                width: 150
            }
        ];
    }

    public get schedulerConfig() {
      return {
        eventRenderer: ({eventRecord, resourceRecord, tplData}: any) => {
            return `<div style="text-overflow:ellipsis;overflow:hidden;white-space:nowrap;">${eventRecord.name} : ${eventRecord.productTypes}</div>`;
        },
        columns: this.columns,
        readOnly: false,
        viewPreset: 'hourAndDay',
        minHeight: '20em',
        rowHeight: 31,
        timeResolution: {
            unit      : 'minute',
            increment : 10
        },
        features: {
          pan: true,
          eventDragSelect: false,
          eventDragCreate: false,
          eventMenu: false,
          eventContextMenu: false,
          eventDrag: {
                showExactDropPosition: true,
                // validatorFn({startDate, endDate, newResource}: any) {
                //     const isValid = !newResource.timeRanges
                //     .filter((x: any) => x.type !== 'site')
                //     .some((x: any) => {
                //         const interval = Interval.fromDateTimes(x.data.startDate, x.data.endDate);
                //         return interval.contains(
                //             DateTime.fromJSDate(startDate)) ||
                //             interval.contains(DateTime.fromJSDate(endDate)
                //         );
                //     });

                //     return {
                //         valid   : isValid,
                //         message : ''
                //     };
                // }
          },
           eventResize: {
               showExactResizePosition: true,
                // validatorFn({startDate, endDate, resourceRecord}: any) {
                //     const isValid = !resourceRecord.timeRanges
                //     .filter((x: any) => x.type !== 'site')
                //     .some((x: any) => {
                //         const interval = Interval.fromDateTimes(x.data.startDate, x.data.endDate);
                //         return interval.contains(
                //             DateTime.fromJSDate(startDate)) ||
                //             interval.contains(DateTime.fromJSDate(endDate)
                //         );
                //     });

                //     return {
                //         valid   : isValid,
                //         message : ''
                //     };
                // }
           },
          eventEdit: false,
          eventTooltip: {
            header: {
              titleAlign: 'start'
            },
            template: this.tooltipTemplate
          },
          resourceTimeRanges: true
        },
        listeners: {
          eventClick: (e: DragEvent<LogisticWorkshopEvent>) => {
            this.selectedOperationId = e.eventRecord.operationId;
          },
          afterEventDrop: async (e: DragEvent<LogisticWorkshopEvent>) => {
              if (e.valid) {
                const item = _.cloneDeep(
                    this.operations.find(x => x.id === e.eventRecords[0].originalData.operationId)
                );
                const oldResource = e.eventRecords[0].originalData.resourceId;
                const newResource = e.eventRecords[0].data.resourceId;

                if (item) {
                    if (oldResource !== newResource) {
                        const firstResource = this.cranes![item.sourceContextId][0];
                        const resource = this.cranes![item.sourceContextId]
                            .find(x => `${x.id}_${item.sourceContextId}` === newResource);
                        item.cranes = item.cranes!.filter(x => `${x.id}_${item.sourceContextId}` !== oldResource);
                        item.cranes.push(resource ? resource : {...firstResource, id: newResource});
                    }
                    item.startTime = DateTime.fromJSDate(e.eventRecords[0].data.startDate!)
                        .toISO({includeOffset: false});
                    const url = `${this.syntaskHost}/api/operations/${item?.id}`;
                    await this._fetchService!.put(url, item);
                    await this.loadEvents([oldResource, newResource]);
                }
              }
          },
          eventResizeEnd: async (e: ResizeEvent<LogisticWorkshopEvent>) => {
              if (e.changed) {
                const item = this.operations.find(x => x.id === e.eventRecord.data.operationId);
                if (item) {
                    item.startTime = DateTime.fromJSDate(e.eventRecord.data.startDate!).toISO({includeOffset: false});
                    item.duration = DateTime.fromJSDate(e.eventRecord.data.endDate!)
                        .diff(DateTime.fromJSDate(e.eventRecord.data.startDate!)).toISO();
                    const url = `${this.syntaskHost}/api/operations/${item?.id}`;
                    await this._fetchService!.put(url, item);
                    await this.loadEvents([e.eventRecord.data.resourceId]);
                }
              }
          }
        }
      };
    }

    public get canApprove() {
        const item = this.operations.find(x => x.id === this.selectedOperationId);
        return item && !item.fixed;
    }

    public get canRemove() {
        const item = this.operations.find(x => x.id === this.selectedOperationId);
        return item?.fixed;
    }

    public async approveEvent() {
        const item = _.cloneDeep(
                this.operations.find(x => x.id === this.selectedOperationId)
            );
        if (item && !item.fixed) {
            const resource = item.cranes!;
            const url = `${this.syntaskHost}/api/operations/${item?.id}`;
            await this._fetchService!.put(url, item);
            await this.loadEvents(resource.map(x => `${x.id}_${item.sourceContextId}`));
            this.selectedOperationId = null;
        }
    }

    public async removeEvent() {
        const item = this.operations.find(x => x.id === this.selectedOperationId);
        if (item?.fixed && confirm(this.$l.Dialog.AreYouSure)) {
            const resource = item.cranes!;
            const url = `${this.syntaskHost}/api/operations/${item?.id}`;
            await this._fetchService!.delete(url);
            await this.loadEvents(resource.map(x => `${x.id}_${item.sourceContextId}`));
            this.selectedOperationId = null;
        }
    }

    public selectSiteHander(e: any) {
        this.currentSite = e.selectedItem;
    }

    public async startRecalc() {
        const contextId = ContextsState.currentContextId;
        const url = `/contexts/${contextId}/startProcessor`;
        await this._fetchService!.post(url);
    }

    private operationToEvents(operation: LogisticOperation): any[] {
        return operation.cranes!
        .map(r => ({
        resourceId: `${r.id}_${operation.sourceContextId}`,
        resourceAlias: r.id,
        resourceName: r.name,
        linkedResource: operation.vehicles?.map(x => x.name).join(', '),
        startDate: DateTime.fromISO(operation.startTime!).toJSDate(),
        endDate: DateTime.fromISO(operation.startTime!).plus(Duration.fromISO(operation.duration)).toJSDate(),
        site: operation.site,
        name: localize(`EventType_${operation.type}`) + ': ' + operation.vehicles?.map(x => x.name).join(', '),
        eventColor: operation.error ? 'red' : this.getOperationColor(operation),
        operationId: operation.id,
        id: operation.id + r.name,
        type: operation.type,
        productTypes: [...new Set(
            operation.packs.reduce((res: string[], x: any) => [...res, x.resourceType], [])
        )].join(', '),
        timeDuration: Duration.fromISO(operation.duration).toFormat('dd.hh:mm:ss'),
        packs: operation.packs,
        areaPlaces: operation.areaPlaces,
        fixed: operation.fixed,
        eventStyle: operation.fixed ? 'plain' : 'hollow'
        }));
    }

    private mergeEvents(operations: LogisticOperation[]) {
        if (operations) {
            const resources = [...new Set(operations
            .reduce((res: any[], x) => [...res, ...x.cranes!.map(r => `${r.id}_${x.sourceContextId}`)], []))];
            this.operations = [...this.operations
            .filter(x => !x.cranes!
                .some(r => resources.some(lr => lr === `${r.id}_${x.sourceContextId}`)))
            , ...operations];
        }
    }

    private async onProcessingFinished({ contextId }: any) {
      if (Number(ContextsState.currentContextId) === Number(contextId)) {
        await this.importOperations(contextId);
      }
    }

    private async importOperations(contextId: any) {
        const url = `${this.syntaskHost}/api/operations/import`;
        if (!contextId) {
            contextId = ContextsState.currentContextId;
        }
        await this._fetchService!.post(url, [contextId]);
        await this.loadResources();
        await this.loadSites();
        await this.loadIntervals();
        await this.loadEvents();
    }

    private async loadEvents(craneAlias?: string[]): Promise<LogisticOperation[]> {
        this.loading = true;
        const contextId = ContextsState.currentContextId;
        const url = `${this.syntaskHost}/api/operations?` + (craneAlias?.reduce((res, c, index) => (res += `craneAliases[${index}]=${c}&`), '') || '');
        this.mergeEvents(await this._fetchService!.get<LogisticOperation[]>(url));
        const result = this.getBounds(
            this.operations || []
        );

        this.startDate = this.startDate || result.startDate.toJSDate();
        this.endDate = this.endDate || result.finishDate.toJSDate();

        this.loading = false;
        return this.operations ? this.operations : [];
    }

    private async loadResources(): Promise<CraneDict> {
        const url = `${this.syntaskHost}/api/dicts/cranes`;
        this.cranes = await this._fetchService!.get<CraneDict>(url);
        return this.cranes;
    }

    private async loadSites(): Promise<SiteDict> {
        const url = `${this.syntaskHost}/api/dicts/sites`;
        this.sites = await this._fetchService!.get<SiteDict>(url);
        return this.sites;
    }

    private async loadIntervals(): Promise<any[]> {
        const contextId = ContextsState.currentContextId;
        const url = `${this.syntaskHost}/api/dicts/intervals`;
        this.intervals = await this._fetchService!.get<any>(url);
        return this.intervals ? this.intervals : [];
    }

    private boundDate(
        value?: string,
        min?: string,
        max?: string
    ): string | undefined {
        if (!value) {
            return value;
        }

        const dateObj = new Date(value);

        if (min && dateObj < new Date(min)) {
            return min;
        }

        if (max && dateObj > new Date(max)) {
            return max;
        }

        return value;
    }

    private getBounds(
        operations: LogisticOperation[]
    ): { startDate: DateTime; finishDate: DateTime } {
        // filter inactive intervals because they often have incredible huge finish date
        const [startDate, finishDate] = operations
            .filter(
                operation => operation.type !== PlannedOperationType.INACTIVE
            )
            .reduce((acc: any[], operation) => {
                const eventStart = DateTime.fromISO(operation.startTime!);
                const eventEnd = eventStart.plus(Duration.fromISO(operation.duration));
                acc[0] =
                    acc[0] === undefined || eventStart < acc[0]
                        ? eventStart
                        : acc[0];
                acc[1] =
                    acc[1] === undefined || eventEnd > acc[1]
                        ? eventEnd
                        : acc[1];
                return acc;
            }, []) as DateTime[];
        return {
            startDate,
            finishDate
        };
    }

    private getOperationColor(operation: LogisticOperation) {
        const colors: { [key in PlannedOperationType]: string } = {
            Loading: 'orange',
            Unloading: 'yellow',
            Relocation: 'green',
            Waiting: 'gray',
            Inactive: 'gray',
            EmptyRelocation: 'teal',
            Preparation: 'pink',
            Completion: 'PaleVioletRed'
        };
        return operation.type && operation.type in colors
            ? colors[operation.type]
            : undefined;
    }

    private tooltipTemplate(data: any) {
        const event = data.eventRecord.data;
        const fields = [];

        // Resource name
        fields.push(`
            <dt>${this.$l.LogisticWorkplace.ResourceColumn}:</dt>
            <dd>
                ${event.resourceName}
            </dd>
        `);

        // Linked resource name
        if (event.linkedResource) {
            fields.push(`
                <dt>${this.$l.LogisticWorkplace.LinkedResourceColumn}:</dt>
                <dd>
                    ${event.linkedResource}
                </dd>
            `);
        }

        // Location
        if (event.site) {
            fields.push(`
                <dt>${this.$l.LogisticWorkplace.LocationColumn}:</dt>
                <dd>
                    ${event.site.name}
                </dd>
            `);
        }

        // Location
        if (event.areaPlaces) {
            fields.push(`
                <dt>${this.$l.LogisticWorkplace.AreaPlacesColumn}:</dt>
                <dd>
                    ${event.areaPlaces}
                </dd>
            `);
        }

        fields.push(`
            <dt>${this.$l.LogisticWorkplace.TimeSpanColumn}:</dt>
            <dd>
                ${event.startDate.toLocaleString()} - ${event.endDate.toLocaleString()}
            </dd>
        `);

        fields.push(`
            <dt>${this.$l.LogisticWorkplace.DurationColumn}:</dt>
            <dd>${data.eventRecord.timeDuration}</dd>
        `);

        if (event.productTypes) {
            fields.push(`
                <dt>${this.$l.LogisticWorkplace.ResourceTypeColumn}:</dt>
                <dd>
                    ${event.productTypes}
                </dd>
            `);
        }

        return `<dl>${fields.join('\n')}</dl>`;
    }
}
